import { requestLaravel } from "../../../../utils/api";
import { CurrentStageStep, IMyProjectList, IProject, IProjectLocationData, IProjectRequest, IProjectStage, ISortProject, ProjectFormData, Stagestep } from "./_models";
import { ApiResponse, IDashboardFilterData, GroupedOption, IElevatorPitchStakeholder, InvestorProspectusDashboardData ,IObjectiveArea, Pagination, StageGateCountData, TimelineTableArea } from "../../core/_models";

export function getProjects(filters: IDashboardFilterData, dashboard_type: string = "none", display_location: string = "No", type: string = "", page?: number, perPage?: number, search?: string, sortKey?: string | null, sortOrder?: "asc" | "desc" | null, is_paginated?: boolean, signal?: AbortSignal) {

  const data = {
    display_location: display_location,
    type: type,
    page: page,
    limit: perPage,
    search: search,
    sort_by: sortKey,
    sort_order: sortOrder,
    is_paginated: is_paginated ? 1 : 0,
    dashboard_type: dashboard_type,
    ...filters,
  };
  return requestLaravel<ApiResponse<{ projects: IProject[], pagination?: Pagination }>>("/projects/get-all-projects", data, { signal });
}

export function downloadProjectListing(filters: IDashboardFilterData, dashboard_type: string = "none", type: string = "", export_type: string = "excel") {
  const data = {
    export_type: export_type,
    dashboard_type: dashboard_type,
    type: type,
    ...filters,
  }
  return requestLaravel<ApiResponse<{ url: string }>>("/projects/export-all-projects", data);
}

export function getStageGatesCounter(filters: IDashboardFilterData, signal?: AbortSignal) {
  const data = {
    ...filters,
  };
  return requestLaravel<ApiResponse<StageGateCountData>>(
    "/dashboard/get-stage-gates-dashboard-data",
    data,
    { signal },
  );
}

export function getCompleteProjects(filters: IDashboardFilterData, page?: number, perPage?: number, search?: string, is_paginated?: boolean, signal?: AbortSignal) {
  const data = {
    ...filters,
    page,
    limit: perPage,
    search,
    is_paginated: is_paginated ? 1 : 0
  };
  return requestLaravel<ApiResponse<{ projects: IProject[] }>>("/projects/completed", data, { signal });
}

export function getProjectStages(project_id: string, signal?: AbortSignal) {
  return requestLaravel<ApiResponse<{ project: ISortProject; stages: IProjectStage[] }>>(
    "/projects/get-project-wise-stages",
    { project_id: project_id },
    { signal },
  );
}

export function getProjectsStagesForApprovals(signal?: AbortSignal) {
  return requestLaravel<ApiResponse<{ list: IProjectRequest[] }>>(
    "/approvers/get-projects-stages-for-approvals",
    {},
    { signal },
  );
}

export function getProjectsActionStages(signal?: AbortSignal) {
  return requestLaravel<ApiResponse<{ list: IProjectRequest[] }>>(
    "/approvers/get-approver-actions",
    {},
    { signal },
  );
}

export function updateProjectsStagesStatus(inputdata: {
  project_id: string | number;
  project_stage_id: string | number;
  action: string;
  remarks?: string;
  user_id?: string | number;
}): Promise<ApiResponse> {
  const { project_id, project_stage_id, action, remarks } = inputdata;
  return requestLaravel<ApiResponse>("/approvers/update-projects-stages-status", {
    project_id,
    project_stage_id,
    action,
    remarks,
  });
}

export function getMyprojects(filters: IDashboardFilterData, page?: number, perPage?: number, search?: string, is_paginated?: boolean, signal?: AbortSignal) {
  const data = {
    page: page,
    limit: perPage,
    search: search,
    is_paginated: is_paginated ? 1 : 0,
    ...filters
  };
  return requestLaravel<ApiResponse<{
    project_data: IMyProjectList[];
    project_pending: number;
    project_completed: number;
    project_evaluated: number;
  }>>("/projects/get-my-projects", data, { signal });
}

export function deleteProjectEvaluator(data: { project_id: number; evaluator_id: number }) {
  return requestLaravel<ApiResponse<unknown>>("/users/delete-evaluator-user", data);
}

export function getInvestorProspectusProjects(filters: IDashboardFilterData, signal?: AbortSignal) {
  const data = {
    ...filters
  };
  return requestLaravel<ApiResponse<{ projects: IProject[] }>>("/investor-prospectus/projects", data, { signal });
}

export function getInvestorProspectusData(filterData: IDashboardFilterData, signal?: AbortSignal) {
  const data = {
    ...filterData
  };
  return requestLaravel<ApiResponse<InvestorProspectusDashboardData>>(
    "/investor-prospectus/dashboard-data",
    data,
    { signal }
  );
}


export function createNewProject(inputData:ProjectFormData) {
  return requestLaravel<ApiResponse>("/projects/create-new-project", inputData);
}

export const findStepIndex = (
  steps: Stagestep[],
  key: string,
  defaultFirst: boolean = false
): CurrentStageStep => {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];

    // Match top-level step
    if (step.key === key) {
      const isFirst = i === 0;
      const isLast = i === steps.length - 1;
      return { index: i, subIndex: -1, step, isFirst, isLast };
    }

    // Match submenu step
    if (step.submenu?.length) {
      const j = step.submenu.findIndex((s) => s.key === key);
      if (j !== -1) {
        const isFirst = i === 0 && j === 0;
        const isLast =
          i === steps.length - 1 && j === step.submenu.length - 1;
        return { index: i, subIndex: j, step: step.submenu[j], isFirst, isLast };
      }
    }
  }
  if (defaultFirst) {
    return { index: 0, subIndex: -1, step: steps[0], isFirst: true, isLast: false };
  }
  return { index: -1, subIndex: -1, step: { key: '', title: '', showIndex: false }, isFirst: true, isLast: true }; // not found
};
export const findNextStep = (
  prev: CurrentStageStep,
  steps: Stagestep[]
): CurrentStageStep => {
  const currentIndex = prev.index;
  const currentSubIndex = prev.subIndex;

  if (currentIndex < 0 || currentIndex >= steps.length) {
    return prev;
  }

  const currentStep = steps[currentIndex];
  const subMenu = currentStep.submenu || [];
  const nextIndex = currentIndex + 1;
  // CASE 1: Inside a submenu
  if (subMenu.length > 0) {
    const nextSubIndex = currentSubIndex + 1;

    //  If another submenu item exists → move to it
    if (nextSubIndex < subMenu.length) {
      return findStepIndex(steps, subMenu[nextSubIndex].key);
    }

    //  If last submenu item → move to the FIRST submenu of next main step (if any)
    if (nextIndex < steps.length) {
      const nextMainStep = steps[nextIndex];
      if (nextMainStep.submenu?.length) {
        return findStepIndex(steps, nextMainStep.submenu[0].key);
      }
      return findStepIndex(steps, nextMainStep.key);
    }
  }

  // CASE 2: At top level step
  if (nextIndex < steps.length) {
    const nextMainStep = steps[nextIndex];
    // If the next main step has no components and has submenu, go to the first submenu item
    if (!nextMainStep.components && nextMainStep.submenu?.length) {
      return findStepIndex(steps, nextMainStep.submenu[0].key);
    }
    return findStepIndex(steps, nextMainStep.key);
  }

  return prev; // no next step
};


export const findPrevStep = (
  prev: CurrentStageStep,
  steps: Stagestep[]
): CurrentStageStep => {
  const currentIndex = prev.index;
  const currentSubIndex = prev.subIndex;

  //  Guard clause for invalid index
  if (currentIndex < 0 || currentIndex >= steps.length) {
    return prev;
  }

  const currentStep = steps[currentIndex];
  const subMenu = currentStep.submenu || [];

  //  CASE 1: Inside a submenu
  if (currentSubIndex !== -1 && subMenu.length > 0) {
    const prevSubIndex = currentSubIndex - 1;

    // If there's a previous submenu item
    if (prevSubIndex >= 0) {
      return findStepIndex(steps, subMenu[prevSubIndex].key);
    }

    if (currentStep.components) {
      return findStepIndex(steps, currentStep.key);
    }


    //  FIXED: If first submenu item → go to *previous main step*
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      const prevMainStep = steps[prevIndex];

      // If that previous main step has submenu → go to its last submenu item
      if (prevMainStep.submenu?.length) {
        const lastSub = prevMainStep.submenu[prevMainStep.submenu.length - 1];
        return findStepIndex(steps, lastSub.key);
      }

      // Otherwise, go directly to that main step
      return findStepIndex(steps, prevMainStep.key);
    }

    // Already at very beginning
    return prev;
  }

  //  CASE 2: On a top-level step
  const prevIndex = Math.max(currentIndex - 1, 0);
  const prevMainStep = steps[prevIndex];

  // If the previous step has a submenu → go to its last submenu item
  if (prevMainStep.submenu?.length) {
    const lastSub = prevMainStep.submenu[prevMainStep.submenu.length - 1];
    return findStepIndex(steps, lastSub.key);
  }

  // Otherwise, go directly to the previous main step
  return findStepIndex(steps, prevMainStep.key);
};


export function getProjectGeoLocations(user_id: string, filters: IDashboardFilterData, dashboard_type: string = "none", search?: string, label?: string, signal?: AbortSignal) {
  const data = {
    user_id: user_id,
    ...filters,
    search: search,
    type: label,
    dashboard_type: dashboard_type
  };
  return requestLaravel<ApiResponse<IProjectLocationData[]>>("/projects/get-project-geo-locations", data, { signal });
}


export function handleUpdateCurrentActiveStepApi(dk_stage: string, active_step: number | string, project_id: number) {
  const data = {
    dk_stage: dk_stage,
    active_step: active_step,
    project_id: project_id,
  };
  return requestLaravel<ApiResponse>("/stage-master/track-active-stage-step", data);
}

type ProjectStageInfoResponse = {
  project: ISortProject;
  stage: IProjectStage;
  stages: IProjectStage[];
}
//  Added During Enhancement
export function getProjectStageInfo(project_id: string, dk_stage: string, signal?: AbortSignal) {
  const data = {
    project_id: project_id,
    stage_dk: dk_stage
  };
  return requestLaravel<ApiResponse<ProjectStageInfoResponse>>("/project-stage-zero/stage/info", data, { signal });
}

export function getProjectEvaluators(project_id: string, signal?: AbortSignal) {
  return requestLaravel<ApiResponse<{ project_evaluators: unknown[]; project_eval_status: string }>>(
    "/project-stage-three/evaluators/get",
    { project_id },
    { signal }
  );
}

export function saveProjectEvaluators(updatedata: { project_id: string | number; user_id?: string | number; evaluator_data: string }) {
  return requestLaravel<ApiResponse<unknown>>(
    "/project-stage-three/evaluators/save",
    updatedata
  );
}

export function update_elivator_pitch(inputdata: any) {
  return requestLaravel<ApiResponse<unknown>>(
    "/project-stage-four/elevator-pitch/update",
    inputdata
  );
}

export type ElevatorPitchFieldUpdate = {
  project_id: string | number;
  elevator_project_overview?: string;
  key_objectives_summary_editor?: string;
  project_status?: string;
  financial_needs?: string;
  private_sector_opportunities?: string;
};

export function updateElevatorPitchFields(data: ElevatorPitchFieldUpdate) {
  return requestLaravel<ApiResponse<unknown>>(
    "/project-stage-four/elevator-pitch/fields/update",
    data
  );
}

export function updateElevatorPitchTimeline(data: {
  project_id: string | number;
  timeline: TimelineTableArea[];
}) {
  return requestLaravel<ApiResponse<unknown>>(
    "/project-stage-four/elevator-pitch/update-timeline",
    data
  );
}

interface IDraftElevatorPitchResponse extends ApiResponse {
  data: {
    elevator_pitch_data: {
      row_id: string | number;
      project_status: string;
      financial_needs: string;
      private_sector_opportunities: string | null;
      key_objectives_summary_editor: string;
      elevator_project_overview: string | null;
      project_overview: string | null;
      estimated_years: string | number;
      estimated_months: string | number;
      estimated_project_start_date: string;
      project_type: string;
      project_size: string;
      project_name: string;
      estimated_cost: string;
      formatted_estimated_project_start_date?: string;
    },
    elevator_pitch_timeline: TimelineTableArea[],
    identity_key_stakeholders: IElevatorPitchStakeholder[],
    objectives: IObjectiveArea[],

  };
}
export function getelevator_pitch(inputdata: any, signal?: AbortSignal): Promise<IDraftElevatorPitchResponse> {
  return requestLaravel<IDraftElevatorPitchResponse>(
    "/project-stage-four/elevator-pitch/get",
    inputdata,
    { signal }
  );
}

export interface ElevatorPitchData {
  row_id: string | number;
  project_status: string;
  financial_needs: string;
  private_sector_opportunities: string;
  key_objectives_summary_editor: string;
  elevator_project_overview: string;
  estimated_years: number;
  estimated_months: number;
  estimated_project_start_date: string;
  project_type: string;
  project_size: string;
  project_name: string;
  estimated_cost: string;
  formatted_estimated_project_start_date: string;
}

export type ElevatorPitchReportStakeholder = IElevatorPitchStakeholder;

interface IDraftElevatorPitchReportResponse extends ApiResponse {
  data: {
    elevator_pitch_data: ElevatorPitchData,
    elevator_pitch_timeline: TimelineTableArea[],
    identity_key_stakeholders: ElevatorPitchReportStakeholder[],
    objectives: IObjectiveArea[],
  };
}


export function getElevatorPitchData(inputdata: any, signal?: AbortSignal): Promise<IDraftElevatorPitchReportResponse> {
  return requestLaravel<IDraftElevatorPitchReportResponse>(
    "/project-stage-four/elevator-pitch/report/get",
    inputdata,
    { signal }
  );
}

export function getProjectInfo(project_id: string) {
  return requestLaravel<ApiResponse<unknown>>(
    "/project-stage-four/project-info/get",
    { project_id }
  );
}

interface IgetStakeHolderGroupedDropdownData extends ApiResponse {
  data: GroupedOption[]
}

export function getStakeHolderGroupedDropdownData(): Promise<IgetStakeHolderGroupedDropdownData> {
  return requestLaravel<IgetStakeHolderGroupedDropdownData>(
    "/stakeholders-master/list",
    {}
  );
}

type IDownloadBusinessCaseReportResponse = ApiResponse<{
  report_url: string;
}>

export const downloadBusinessCaseReport = (project_id: string) => {
  return requestLaravel<IDownloadBusinessCaseReportResponse>(
    "/project-reports/executive-summary",
    { project_id }
  );

}